/**
 * The two documents, displayed.
 *
 * ── WHY A PANE AND NOT A LINK ─────────────────────────────────────────────
 * A click-through binds an institution when the person clicking had a real
 * opportunity to read what they agreed to and the page said plainly what the
 * click would do. The design was reasoned under Illinois law, where Sgouros
 * v. TransUnion (7th Cir. 2016) controlled, and its holding is about exactly
 * this: text the user was never actually shown, and a button whose own label
 * did not say it was an agreement. Governing law moved to Louisiana (Fifth
 * Circuit) on Sep 12 2026; the principle is general to clickwrap, and counsel
 * should confirm it for the new forum.
 *
 * So both documents are ON THE PAGE, in a scrollable pane, one tab each. The
 * checkbox that follows names both by version. The button says "Start
 * subscription", never "Continue". Nothing promotional sits between the pane,
 * the box and the button. (Nicosia v. Amazon, on clutter.)
 *
 * ── SCROLLING IS RECORDED, NOT REQUIRED ───────────────────────────────────
 * Forcing a reader to the bottom of a pane before the box unlocks adds real
 * friction, is worse on a phone, and no case requires it. What we do instead
 * is record whether they reached the end of each document, and store that
 * alongside the acceptance. It is evidence if it is ever needed, and it costs
 * the buyer nothing.
 *
 * A document short enough to fit without scrolling counts as read to the end
 * the moment it is shown, which is why the measurement runs on mount and on
 * resize rather than only on a scroll event that will never fire.
 *
 * ── KEYBOARD ──────────────────────────────────────────────────────────────
 * A real tablist: one Tab stop across the tabs, arrows move and select, Home
 * and End jump. The pane itself is focusable, because a scrollable region
 * that a keyboard cannot scroll is a trap.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "~/app/lib/cn";
import { KEY_TERMS, LEGAL } from "~/app/lib/pricing";
import {
  DOCUMENT_TABS,
  DSA_CLAUSES,
  type DocumentTabId,
} from "~/app/components/checkout/checkout-model";

export interface DocumentPanelProps {
  /** Told which documents have been read to the end, as they are. */
  onReadToEnd: (tab: DocumentTabId) => void;
  readToEnd: { terms: boolean; addendum: boolean };
  className?: string;
}

/**
 * The key terms in CLAUSE order (Sep 15 2026). `KEY_TERMS` is ranked by what
 * matters most to a buyer, which is right for the pages that list a few of
 * them, and read as a jumble here: 10.1, 11.3, 14.3, 15.1, then 7.1.
 */
const TERMS_IN_CLAUSE_ORDER = [...KEY_TERMS].sort((a, b) => {
  const [aSection = 0, aClause = 0] = a.clause.split(".").map(Number);
  const [bSection = 0, bClause = 0] = b.clause.split(".").map(Number);
  return aSection - bSection || aClause - bClause;
});

export function DocumentPanel({ onReadToEnd, readToEnd, className }: DocumentPanelProps) {
  const [tab, setTab] = useState<DocumentTabId>("terms");
  const paneRef = useRef<HTMLDivElement>(null);

  /* THE PANE KEPT JUMPING BACK TO THE TOP (found Sep 15 2026, Thomas: "endless
     scroll"). The checkout passes a new `onReadToEnd` on every render; reaching
     the end called it, the checkout re-rendered, `measure` changed identity,
     and the effect that set up the pane ran again and reset `scrollTop` to 0.
     So every arrival at the bottom threw the reader back to the first clause.
     The callback is read through a ref, `measure` depends on the tab alone,
     and the scroll position resets only when the reader changes tab. */
  const onReadToEndRef = useRef(onReadToEnd);
  onReadToEndRef.current = onReadToEnd;

  /** Within eight pixels of the bottom counts, because sub-pixel layout exists. */
  const measure = useCallback(() => {
    const pane = paneRef.current;
    if (!pane) return;
    const atEnd = pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 8;
    if (atEnd) onReadToEndRef.current(tab);
  }, [tab]);

  // A fresh document starts at the top: on a tab change, and never otherwise.
  useEffect(() => {
    if (paneRef.current) paneRef.current.scrollTop = 0;
  }, [tab]);

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [measure]);

  const move = (delta: number): void => {
    const at = DOCUMENT_TABS.findIndex((entry) => entry.id === tab);
    const next = DOCUMENT_TABS[(at + delta + DOCUMENT_TABS.length) % DOCUMENT_TABS.length];
    if (next) setTab(next.id);
  };

  const active = DOCUMENT_TABS.find((entry) => entry.id === tab) ?? DOCUMENT_TABS[0];

  return (
    <div className={cn("eg-docpane", className)}>
      <div className="eg-docpane-tabs" role="tablist" aria-label="The documents you are agreeing to">
        {DOCUMENT_TABS.map((entry) => {
          const done = entry.id === "terms" ? readToEnd.terms : readToEnd.addendum;
          return (
            <button
              key={entry.id}
              type="button"
              role="tab"
              id={`doc-tab-${entry.id}`}
              aria-selected={tab === entry.id}
              aria-controls="doc-pane"
              tabIndex={tab === entry.id ? 0 : -1}
              className="eg-docpane-tab"
              onClick={() => setTab(entry.id)}
              onKeyDown={(event) => {
                if (event.key === "ArrowRight") {
                  event.preventDefault();
                  move(1);
                } else if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  move(-1);
                } else if (event.key === "Home") {
                  event.preventDefault();
                  const first = DOCUMENT_TABS[0];
                  if (first) setTab(first.id);
                } else if (event.key === "End") {
                  event.preventDefault();
                  const last = DOCUMENT_TABS[DOCUMENT_TABS.length - 1];
                  if (last) setTab(last.id);
                }
              }}
            >
              <span>{entry.label}</span>
              {done ? (
                <span className="eg-docpane-read">
                  Read
                  <span className="e-sr-only"> to the end</span>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        ref={paneRef}
        id="doc-pane"
        role="tabpanel"
        tabIndex={0}
        aria-labelledby={`doc-tab-${tab}`}
        className="eg-docpane-body"
        onScroll={measure}
      >
        {tab === "terms"
          ? TERMS_IN_CLAUSE_ORDER.map((term) => (
              <article className="eg-clause" key={term.clause}>
                <h4>
                  <span className="eg-clause-ref">{term.clause}</span>
                  {term.heading}
                </h4>
                <p>{term.text}</p>
              </article>
            ))
          : DSA_CLAUSES.map((clause) => (
              <article className="eg-clause" key={clause.ref}>
                <h4>
                  <span className="eg-clause-ref">{clause.ref}</span>
                  {clause.heading}
                </h4>
                {clause.body.map((paragraph) => (
                  <p key={paragraph.slice(0, 48)}>{paragraph}</p>
                ))}
              </article>
            ))}

        <p className="eg-docpane-end">
          {tab === "terms"
            ? `These are the clauses of the Enterprise Subscription Terms ${LEGAL.termsVersion} a buyer is most often surprised by later. Open the full document for the rest.`
            : `This is the operative part of the Data Sharing Addendum ${LEGAL.dsaVersion}. Open the full document for the rest.`}
        </p>
      </div>

      <p className="eg-docpane-foot">
        <a href={active?.href ?? LEGAL.termsUrl} target="_blank" rel="noreferrer">
          Open {active?.label ?? "the document"} in full
        </a>
        <span aria-hidden="true"> · </span>
        <span>
          Every published version keeps its own permanent address and is never
          edited after publication, so you can always prove what you accepted.
        </span>
      </p>
    </div>
  );
}
